import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Box, Tab } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { closeQualificationDrawer } from '../../../store/features/qualification/qualificationSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getEligibleModuleUrl } from '../../../utils/qualification';
import { QualificationInputUpsertFormContainer } from '../shared/QualificationInputUpsertFormContainer';
import { QualificationWithAddressInputs } from '../types';
import { GuestUserQualificationCreateForm } from './qualification-create-form/GuestUserQualificationCreateForm';
import { QualificationFindFormContainer } from './qualification-find-form/QualificationFindFormContainer';
import { guestUserQualificationFormStyles } from './styles';
import { GuestUserQualificationTab } from './types';

export const GuestUserQualificationForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { guestUser, qualificationFormData, selectedModule } = useAppSelector((state) => {
    return {
      guestUser: state.qualification.guestUser,
      selectedModule: state.qualification.selectedModule,
      qualificationFormData: state.qualification.guestQualification
        ? ({
            ...state.qualification.guestQualification?.qualification,
            address: state.qualification.guestQualification?.address_coordinates_unified.location,
            address_lat: state.qualification.guestQualification?.address_coordinates_unified.lat,
            address_lng: state.qualification.guestQualification?.address_coordinates_unified.lng,
          } as unknown as Partial<QualificationWithAddressInputs>)
        : undefined,
    };
  });
  const [currentTab, setCurrentTab] = useState<GuestUserQualificationTab>('FIND_TAB');
  const [errorMessage, setErrorMessage] = useState('');

  const handleGuestUserWithoutQualification = useCallback((): void => {
    setErrorMessage('Qualification could not be found for your email address.');
  }, []);
  const clearError = (): void => {
    setErrorMessage('');
  };

  const gotoSelectedModuleOnSuccess = (): void => {
    if (selectedModule) {
      const url = getEligibleModuleUrl(selectedModule);
      navigate(url);
    }
    dispatch(closeQualificationDrawer());
  };

  return (
    <Box>
      {guestUser ? (
        <Box sx={{ px: 2.5 }}>
          {errorMessage.trim().length > 0 && (
            <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <QualificationInputUpsertFormContainer
            onFormSubmit={clearError}
            email={guestUser.email}
            mode={qualificationFormData ? 'EDIT' : 'CREATE'}
            defaultValues={qualificationFormData}
            onUpsert={gotoSelectedModuleOnSuccess}
          />
        </Box>
      ) : (
        <TabContext value={currentTab}>
          <Box sx={guestUserQualificationFormStyles.tabContainer}>
            <TabList
              onChange={(_event, newValue) => setCurrentTab(newValue)}
              aria-label="Guest user eligibility qualification input tabs"
            >
              <Tab sx={guestUserQualificationFormStyles.tab} label="Find qualification" value="FIND_TAB" />
              <Tab sx={guestUserQualificationFormStyles.tab} label="Create qualification" value="CREATE_TAB" />
            </TabList>
          </Box>
          <TabPanel value="FIND_TAB">
            <QualificationFindFormContainer
              onFoundGuestUser={gotoSelectedModuleOnSuccess}
              onGuestUserWithoutQualificationFound={handleGuestUserWithoutQualification}
            />
          </TabPanel>
          <TabPanel value="CREATE_TAB">
            {errorMessage.trim().length > 0 && (
              <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <GuestUserQualificationCreateForm onGuestUserCreate={gotoSelectedModuleOnSuccess} onSubmit={clearError} />
          </TabPanel>
        </TabContext>
      )}
    </Box>
  );
};
